import React, { useState } from "react";
import { connect } from "react-redux";
import { Actions as EMPLOYEEACTIONS } from "../../../store/actions/employeeActions";
import DatePicker from "react-datepicker";
import { DateInputFormat, handleRawDate } from "../../common/DateComponents";
import { format, addDays, isAfter, addYears, isValid, max, set } from "date-fns";
import { XMarkIcon } from "@heroicons/react/20/solid";
import Spinner from "../../common/Spinner";
import { useEffect } from "react";
import { min } from "date-fns";
import { useMemo } from "react";
import BuddySelector from "@/components/common/custom-selectors/BuddySelector";
import Modal from "@/common/Modal";

const parseDates = (dates) => {
	if (!dates) return [];

	return dates
		.map((d) => {
			if (!d) return null;
			if (d instanceof Date) return d;
			else return new Date(d);
		})
		.filter((d) => isValid(d));
};

const yearList = (() => {
	const yearList = [];
	const differenceInYears = new Date().getFullYear() - 2023;
	for (let i = 0; i <= differenceInYears; i++) {
		yearList.push(2023 + i);
	}
	return yearList.map((year) => ({ value: year, label: year, taxYear: `${year}/${year + 1}` }));
})();

const ConfirmModal = ({
	employee,
	modalStatus,
	modalType,
	closeModal,
	confirmChange,
	modalSaving,
	modalSaved,
	cannotReplace,
	minDate,
	maxDate,
	fixedDate,
	previousDate,
	engagementDate,
	terminationDate,
    calculateChangeImpact,
    backPayMessage,
	cannotCreateNew,
	dateType,
}) => {
	const lockDate = previousDate ? addDays(new Date(previousDate), 1) : null;
	const minStartDate = useMemo(
		() => max(parseDates([minDate, engagementDate, lockDate])),
		[minDate, engagementDate, lockDate],
	);
	const maxEndDate = useMemo(() => min(parseDates([terminationDate, maxDate])), [terminationDate, maxDate]);
	const [internalState, setInternalState] = useState("initial");
	const [fromDate, setFromDate] = useState();

	useEffect(() => {
		if (fixedDate) {
			setFromDate(new Date(fixedDate));
			setInternalState("new");
		} else {
			setInternalState("initial");
			setFromDate(null);
		}
	}, [modalStatus]);

	return (
		<Modal
			status={modalStatus}
			close={closeModal}
			title={`Confirm ${employee.givenName}'s ${modalType} Update`}
		>
			{modalSaved && (
				<div
					className="block-content"
					style={{ padding: 20 }}
				>
					<p>
						{employee.givenName}'s {modalType} changes have been saved.
					</p>

					<button
						className="btn btn-success pull-right"
						onClick={() => closeModal()}
					>
						Close
					</button>
					<div className="clearfix" />
				</div>
			)}
			{!modalSaved && internalState == "initial" && (
				<div
					className="block-content"
					style={{ padding: 20 }}
				>
					<p>
						You're about to update {employee.givenName}'s {modalType}. You can either replace the previous
						values, or have the change take effect on another date.
					</p>

					<div
						style={{
							display: "flex",
							flexDirection: "row",
						}}
					>
						<div
							style={{
								width: "40%",
								textAlign: "center",
							}}
						>
							<button
								className="btn btn-success"
								type="button"
								disabled={cannotReplace}
								onClick={() => setInternalState("replace")}
							>
								Replace {modalType}
							</button>
						</div>
						<div
							style={{
								flexGrow: 2,
								alignSelf: "center",
							}}
						>
							<div
								style={{
									display: "flex",
									flexDirection: "row",
									alignItems: "center",
								}}
							>
								<hr
									style={{
										flexGrow: 1,
										margin: 5,
									}}
								/>
								<span>or</span>
								<hr
									style={{
										flexGrow: 1,
										margin: 5,
									}}
								/>
							</div>
						</div>

						<div
							style={{
								width: "40%",
								textAlign: "center",
							}}
						>
							<button
								className="btn btn-success"
								onClick={() => setInternalState("new")}
								disabled={cannotCreateNew}
								type="button"
							>
								Create a new {modalType}
							</button>
						</div>
					</div>
				</div>
			)}
			{!modalSaved && internalState == "new" && (
				<div className="block-content">
					<p>
						From when would you like {employee.givenName}'s new {modalType} to take effect?
					</p>
					{["taxYear", "year"].includes(dateType) && (
						<BuddySelector
							options={yearList.map((year) => ({
								value: year.value,
								label: year[dateType === "taxYear" ? "taxYear" : "label"],
							}))}
							value={yearList.find((year) => (fromDate && format(fromDate, "yyyy")) == year.value)}
							onChange={(selected) => {
								if (selected) {
									const selectedYear = selected.value;
									const selectedDate = set(new Date(), { year: selectedYear });
									setFromDate(selectedDate);
								}
							}}
							placeholder="Select Tax Year"
						/>
					)}
					{dateType === "date" && (
						<DatePicker
							selected={fromDate}
							onChangeRaw={(e) => {
								handleRawDate(e.target.value, (date) => {
									if (isAfter(date, addYears(new Date(), 100)) || date < lockDate) return;
									if (isValid(date)) setFromDate(date);
								});
							}}
							onChange={(newFromDate) => {
                        		calculateChangeImpact(format(newFromDate, "yyyy-MM-dd"));
								setFromDate(newFromDate);
							}}
							disabled={!!fixedDate}
							minDate={minStartDate}
							maxDate={maxEndDate}
							dateFormat="do MMM yyyy"
							customInput={<DateInputFormat />}
							strictParsing
							autoComplete="off"
						/>
					)}

                    {backPayMessage}

					<div className="block buttons">
						{!modalSaving && (
							<>
								<button
									className="btn btn-danger pull-left"
									onClick={() => setInternalState("initial")}
									style={{ marginLeft: 0 }}
								>
									Go Back
								</button>
								<button
									className="btn btn-success pull-right"
									onClick={() => {
										confirmChange(format(fromDate, "yyyy-MM-dd"), false, !!backPayMessage);
										setInternalState("initial");
									}}
									style={{ marginRight: 0 }}
									disabled={!isValid(fromDate)}
								>
									Confirm new {modalType}
								</button>
							</>
						)}
						{modalSaving && (
							<button
								className="btn btn-success pull-right"
								style={{ marginRight: 0 }}
							>
								<Spinner
									style={{ height: "1.5em", verticalAlign: "middle", float: "left" }}
									className="animate-spin -ml-1 mr-3 h-5 w-5 "
								/>
								<div style={{ float: "left" }}>Saving</div>
							</button>
						)}

						<div className="clearfix" />
					</div>
				</div>
			)}
			{!modalSaved && internalState == "replace" && (
				<div className="block-content">
					<p>
						You are chosing to replace {employee.givenName}'s previous {modalType}. Be aware that if you
						have already payslips using the current setting, these may have different values upon
						recalculation.
					</p>{backPayMessage}
					<div className="clearfix" />

					<div className="block buttons">
						{!modalSaving && (
							<React.Fragment>
								<button
									className="btn btn-danger pull-left"
									onClick={() => setInternalState("initial")}
									style={{ marginLeft: 0 }}
								>
									Go Back
								</button>
								<button
									className="btn btn-success pull-right"
									onClick={() => {
										confirmChange(null, true, !!backPayMessage);
										setInternalState("initial");
									}}
									style={{ marginRight: 0 }}
								>
									Yes, I understand
								</button>
							</React.Fragment>
						)}
						{modalSaving && (
							<button
								className="btn btn-success pull-right"
								style={{ marginRight: 0 }}
							>
								<Spinner
									style={{ height: "1.5em", verticalAlign: "middle", float: "left" }}
									className="animate-spin -ml-1 mr-3 h-5 w-5 "
								/>
								<div style={{ float: "left" }}>Saving</div>
							</button>
						)}

						<div className="clearfix" />
					</div>
				</div>
			)}
		</Modal>
	);
};

const mapStateToProps = (state) => {
	const { engagementDate, terminationDate } = state.employee?.agreementInfo || {};
	const {
		status: modalStatus,
		saving: modalSaving,
		saved: modalSaved,
		type: modalType,
		minDate,
		maxDate,
		fixedDate,
		previousDate,
		cannotReplace,
		cannotCreateNew,
		dateType,
	} = state.employee?.confirmModal || {};

  const backPayAmount = state?.employee?.backpay?.data?.backPayDue;
  let backPayMessage = null;
  const currency = state.company?.settings?.currency;

  if (backPayAmount){
    backPayMessage = <p>This change will result in a backpay of <strong>{currency}{backPayAmount.toFixed(2)}</strong> for the affected employee.</p>
  } else if (state?.employee?.backpay?.calculating){
    backPayMessage = <p>Calculating backpay...</p>
  }

  return {
		employee: state.employee?.agreementInfo?.employeeDetails,
		modalStatus,
		modalSaving,
		modalSaved,
		modalType,
		minDate,
		maxDate,
		fixedDate,
		previousDate,
		cannotReplace,
		cannotCreateNew,
		dateType,
		engagementDate: engagementDate ? new Date(engagementDate) : null,
		terminationDate: terminationDate && terminationDate !== "null" ? new Date(terminationDate) : null,
        backPayMessage,
	};
};

const mapDispatchToProps = dispatch => ({
  closeModal: () => dispatch(EMPLOYEEACTIONS.confirmModal({ status: false })),
  confirmChange: (date, replace, processBackpay) => dispatch(EMPLOYEEACTIONS.confirmChange({ date, replace, processBackpay })),
  calculateChangeImpact: (date) => dispatch(EMPLOYEEACTIONS.calculateChangeImpact({ date })),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(ConfirmModal)
